import { AlertBanner, Spinner } from '@curri/components'

import {
  fetchBookingRequest,
  type Booking,
  type BookingRequest,
} from '../../api/bookings.ts'
import { useApiResource } from '../../api/useApiResource.ts'
import { useApiFetch } from '../../ghost/GhostProvider.tsx'
import { PANEL, PayloadPreview } from '../webhooks/PayloadPreview.tsx'

import {
  describeFailure,
  describeRequestReason,
  requestFidelityLabel,
} from './format.ts'

// The expanded row's "originating request" console: the request the partner
// actually sent, read back from the request logs by the service (INT-488).
// Fetched lazily — the component mounts only when a row opens — and rendered
// only for Curri users (see BookingDetail), because the body comes back with
// the fidelity caveats the `meta` label names and a partner should not be
// handed a "request" that may not be byte-exact.
//
// Every way of not having the request is a sentence, never a substitute body.

const HEADING = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--curri-text-tertiary)',
} as const

const EmptyState = ({ children }: { children: string }) => (
  <div
    style={{
      border: '1px solid var(--curri-border)',
      borderRadius: 12,
      padding: '14px 16px 16px',
    }}
  >
    <div style={HEADING}>Originating request</div>
    <p
      style={{
        margin: '10px 0 0',
        fontSize: 12.5,
        color: 'var(--curri-text-tertiary)',
      }}
    >
      {children}
    </p>
  </div>
)

const requestSubheader = (request: BookingRequest) => (
  <>
    <span style={{ color: PANEL.string }}>{request.method}</span>
    <span style={{ color: PANEL.subdued }}>{request.path}</span>
    {request.operationName !== undefined && (
      <span style={{ color: PANEL.label }}>· {request.operationName}</span>
    )}
    <span style={{ flex: 1 }} />
    {request.curriVersion !== undefined && (
      <span style={{ color: PANEL.label }}>Curri-Version: {request.curriVersion}</span>
    )}
  </>
)

const requestFooter = (request: BookingRequest) => {
  const outcome = [
    request.statusCode === undefined ? null : String(request.statusCode),
    request.durationMs === undefined ? null : `${request.durationMs} ms`,
    request.requestId === undefined ? null : request.requestId,
  ].filter((part): part is string => part !== null)

  return (
    <>
      <span style={{ color: PANEL.subdued }}>{outcome.join(' · ')}</span>
      <span style={{ flex: 1 }} />
      {request.userAgent !== undefined && (
        <span
          style={{
            color: PANEL.label,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={request.userAgent}
        >
          via {request.userAgent}
        </span>
      )}
    </>
  )
}

export const BookingRequestPanel = ({ booking }: { booking: Booking }) => {
  const apiFetch = useApiFetch()
  const resource = useApiResource(
    signal => fetchBookingRequest(apiFetch, booking.id, signal),
    [apiFetch, booking.id]
  )

  if (resource.status === 'loading') {
    return (
      <div
        style={{
          border: '1px solid var(--curri-border)',
          borderRadius: 12,
          padding: '14px 16px 16px',
        }}
      >
        <div style={HEADING}>Originating request</div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 14 }}>
          <Spinner size="sm" />
        </div>
      </div>
    )
  }

  if (resource.status === 'failed') {
    return (
      <AlertBanner type={describeFailure(resource.failure).tone}>
        {describeFailure(resource.failure).body}
      </AlertBanner>
    )
  }

  if (resource.data.request === null) {
    return <EmptyState>{describeRequestReason(resource.data.reason)}</EmptyState>
  }

  const { request } = resource.data
  return (
    <PayloadPreview
      footer={requestFooter(request)}
      maxHeight={360}
      meta={requestFidelityLabel(request.bodyFidelity)}
      payload={request.body}
      subheader={requestSubheader(request)}
      title="Originating request"
    />
  )
}
